import { ConnectionTypeEnum, MessageDirectionEnum } from "../../Premises/types";
import { AlertTones } from "@origin-digital/ods-types/.";
import { ServiceType } from "../types";

export enum MessageTypeEnum {
  STANDARD = "STANDARD",
  CLOSE_AND_TRANSFER = "CLOSE_AND_TRANSFER",
  INTERNET = "INTERNET",
}

export interface IMessageMetadata {
  requestId?: string;
  journeyId?: string;
  rawRequestStatus?: string;
  latestMessageDirection?: MessageDirectionEnum;
  latestMessageProcessed?: boolean;
  latestMessageType?: MessageTypeEnum;
  hasUnreadMessageForUser?: boolean;
  messagePause?: boolean;
  messageEnabled?: boolean;
  serviceType?: ServiceType;
  connectionType?: ConnectionTypeEnum;
  type: MessageTypeEnum;
}

export const initialMessageMetadata: IMessageMetadata = {
  messageEnabled: false,
  type: MessageTypeEnum.STANDARD,
};

export const initialInternetMessageMetadata: IMessageMetadata = {
  messageEnabled: false,
  type: MessageTypeEnum.INTERNET,
};

export interface IMessageRest {
  id?: string;
  journey_id: string;
  text: string;
  author_email: string;
  author_name: string;
  direction: MessageDirectionEnum;
  created: number;
  updated?: number | null;
  updater?: string | null;
  processed: boolean;
  type: MessageTypeEnum;
  attachments_meta?: IMessageAttachmentRest[] | null;
}

export interface IMessageAttachmentRest {
  id?: string;
  file_upload_type?: MessageDocumentTypeEnum;
  file_upload_type_affix?: string;
  file_name: string;
  link?: string | null;
}

export type MessageAttachment = {
  id?: string;
  fileUploadType?: MessageDocumentTypeEnum;
  fileUploadTypeAffix?: string;
  fileName: string;
  link?: string | null;
  file?: File;
};

export type MessageRecord = {
  id?: string;
  journeyId: string;
  text: string;
  authorEmail: string;
  authorName: string;
  direction: MessageDirectionEnum;
  created: Date;
  updated?: Date | null;
  updater?: string | null;
  processed: boolean;
  type: MessageTypeEnum;
  attachmentsMeta?: MessageAttachment[] | null;
};

export type MessageRecords = MessageRecord[];

export interface IMessageSnackbar {
  tone?: AlertTones;
  message?: string;
}

export enum MessageDocumentTypeEnum {
  ELECTRICAL_WORKS_REQUEST = "ELECTRICAL_WORKS_REQUEST",
  CERTIFICATE_OF_ELECTRICAL_SAFETY = "CERTIFICATE_OF_ELECTRICAL_SAFETY",
  ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY = "ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY",
  MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY = "MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY",
  SITE_PLAN = "SITE_PLAN",
  PHOTOS = "PHOTOS",
  QUOTE = "QUOTE",
  PLAN_OF_SUBDIVISION = "PLAN_OF_SUBDIVISION",
  NEW_ENERGY_ACCOUNT_FORM = "NEW_ENERGY_ACCOUNT_FORM",
  HANDOVER_CONSENT_FORM = "HANDOVER_CONSENT_FORM",
  SOLAR_PV_AGREEMENT = "SOLAR_PV_AGREEMENT",
  VESI_ABOLISHMENT_FORM = "VESI_ABOLISHMENT_FORM",
  APPLIANCE_CHECKLIST = "APPLIANCE_CHECKLIST",
  OTHER = "OTHER",
}

interface IMessageDocumentType {
  type: MessageDocumentTypeEnum;
  label: string;
}

export const MessageDocumentTypes: {
  [key in MessageDocumentTypeEnum]: IMessageDocumentType;
} = {
  [MessageDocumentTypeEnum.ELECTRICAL_WORKS_REQUEST]: {
    type: MessageDocumentTypeEnum.ELECTRICAL_WORKS_REQUEST,
    label: "Electrical Works Request",
  },
  [MessageDocumentTypeEnum.CERTIFICATE_OF_ELECTRICAL_SAFETY]: {
    type: MessageDocumentTypeEnum.CERTIFICATE_OF_ELECTRICAL_SAFETY,
    label: "Certificate of Electrical Safety",
  },
  [MessageDocumentTypeEnum.ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY]: {
    type: MessageDocumentTypeEnum.ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY,
    label: "Additional Certificate of Electrical Safety",
  },
  [MessageDocumentTypeEnum.MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY]: {
    type: MessageDocumentTypeEnum.MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY,
    label: "Mains Certificate of Electrical Safety",
  },
  [MessageDocumentTypeEnum.SITE_PLAN]: {
    type: MessageDocumentTypeEnum.SITE_PLAN,
    label: "Site Plan",
  },
  [MessageDocumentTypeEnum.PHOTOS]: {
    type: MessageDocumentTypeEnum.PHOTOS,
    label: "Photos",
  },
  [MessageDocumentTypeEnum.QUOTE]: {
    type: MessageDocumentTypeEnum.QUOTE,
    label: "Quote",
  },
  [MessageDocumentTypeEnum.PLAN_OF_SUBDIVISION]: {
    type: MessageDocumentTypeEnum.PLAN_OF_SUBDIVISION,
    label: "Plan of Subdivision",
  },
  [MessageDocumentTypeEnum.NEW_ENERGY_ACCOUNT_FORM]: {
    type: MessageDocumentTypeEnum.NEW_ENERGY_ACCOUNT_FORM,
    label: "New energy account form",
  },
  [MessageDocumentTypeEnum.HANDOVER_CONSENT_FORM]: {
    type: MessageDocumentTypeEnum.HANDOVER_CONSENT_FORM,
    label: "Close and transfer form",
  },
  [MessageDocumentTypeEnum.SOLAR_PV_AGREEMENT]: {
    type: MessageDocumentTypeEnum.SOLAR_PV_AGREEMENT,
    label: "Photovoltaic Solar Generator Connection Agreement",
  },
  [MessageDocumentTypeEnum.VESI_ABOLISHMENT_FORM]: {
    type: MessageDocumentTypeEnum.VESI_ABOLISHMENT_FORM,
    label: "VESI Abolishment Form",
  },
  [MessageDocumentTypeEnum.APPLIANCE_CHECKLIST]: {
    type: MessageDocumentTypeEnum.APPLIANCE_CHECKLIST,
    label: "Natural Gas Appliances Checklist",
  },
  [MessageDocumentTypeEnum.OTHER]: {
    type: MessageDocumentTypeEnum.OTHER,
    label: "Other",
  },
};

export const ALLOWED_MESSAGE_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel", //XLS
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", //XLSX
  "application/vnd.ms-excel.sheet.macroenabled.12", //XLSM
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const MESSAGE_CHAR_LIMIT = 99999;
